import React from 'react';
import { usesTableMap } from '../../utils/roomMode.ts';
import {
  Armchair,
  CalendarClock,
  CheckCircle,
  Hourglass,
  LayoutGrid,
  LogOut,
  Table,
  Users,
  UserX,
} from 'lucide-react';
import WaitlistPanel from './WaitlistPanel.tsx';
import { extractMesaIdsFromOcupacoes } from '../../utils/reservationPeople.ts';
import type { MesaOcupacao, SalaOcupacao } from '../../services/api.ts';
import { buildWhatsappLink, WHATSAPP_ICON_URL } from '../../../../../utils/whatsapp';

type TodayReservationTab = 'upcoming' | 'seated' | 'finalized';
type BoardFilter = 'both' | 'reservations' | 'waitlist';

type TodayTab = {
  id: TodayReservationTab;
  label: string;
  count: number;
};

type DashboardTodayBoardProps = {
  effectiveBoardFilter: BoardFilter;
  reservationsFeatureEnabled: boolean;
  waitlistFeatureEnabled: boolean;
  dashboardSummary: { reservationsToday: number };
  waitlistCount: number;
  setBoardFilter: (value: BoardFilter) => void;
  requiresClientConfirmation: boolean;
  todayConfirmedOnly: boolean;
  setTodayConfirmedOnly: (value: boolean) => void;
  todayRoomFilter: string;
  setTodayRoomFilter: (value: string) => void;
  floors: any[];
  setShowReservationModal: (value: boolean) => void;
  selectedDateLabel: string;
  todayReservationTabs: TodayTab[];
  todayReservationTab: TodayReservationTab;
  setTodayReservationTab: (value: TodayReservationTab) => void;
  activeTodayTab: TodayTab | undefined;
  activeTodayReservations: any[];
  todayReservationsEmptyMessage: string;
  awaitingOcupacoesByReservationId: Map<string, MesaOcupacao[]>;
  seatedOcupacoesByReservationId: Map<string, MesaOcupacao[]>;
  salaOcupacaoByReservationId: Map<string, SalaOcupacao>;
  usarMapaMesas: boolean;
  resolveTableDisplayNames: (ids: Array<string | number | null | undefined>) => string | null;
  getReservationStatusMeta: (status: any, requiresConfirmation: boolean) => {
    displayStatus: string;
    effectiveStatus: string;
    normalizedStatus: string;
  };
  requiresRestaurantConfirmation: boolean;
  isReservationCustomerConfirmed: (status?: string | null) => boolean;
  isUnavailableReservationStatus: (status?: string | null) => boolean;
  handleEditReservation: (reservationId: string | number) => void;
  STATUS_LABELS: Record<string, string>;
  statusUpdatingId: string | null;
  openMesaSelectModal: (res: any, mode: 'seat' | 'select' | 'leave' | 'waitlist-seat') => void;
  openSalaReleaseModal: (ocupacao: SalaOcupacao) => void;
  requestReservationRoomSeat: (res: any) => void;
  handleReleaseReservationTables: (reservationId: string, mesaIds: string[]) => Promise<void> | void;
  handleFinalizeReservationService: (reservationId: string, mesaIds: string[]) => Promise<void> | void;
  setNoShowConfirmation: (value: {
    reservationId: string;
    reservationName: string;
    reservationTime: string;
  } | null) => void;
  selectedDateKey: string;
  setWaitlistCount: (count: number) => void;
  openWaitlistSeatModal: (entry: any) => void;
  waitlistRefreshKey: number | string;
};

const DashboardTodayBoard = (props: DashboardTodayBoardProps) => {
  const {
    effectiveBoardFilter,
    reservationsFeatureEnabled,
    waitlistFeatureEnabled,
    dashboardSummary,
    waitlistCount,
    setBoardFilter,
    requiresClientConfirmation,
    todayConfirmedOnly,
    setTodayConfirmedOnly,
    todayRoomFilter,
    setTodayRoomFilter,
    floors,
    setShowReservationModal,
    selectedDateLabel,
    todayReservationTabs,
    todayReservationTab,
    setTodayReservationTab,
    activeTodayTab,
    activeTodayReservations,
    todayReservationsEmptyMessage,
    awaitingOcupacoesByReservationId,
    seatedOcupacoesByReservationId,
    salaOcupacaoByReservationId,
    usarMapaMesas: legacyMapDefault,
    resolveTableDisplayNames,
    getReservationStatusMeta,
    requiresRestaurantConfirmation,
    isReservationCustomerConfirmed,
    isUnavailableReservationStatus,
  handleEditReservation,
  STATUS_LABELS,
  statusUpdatingId,
    openMesaSelectModal,
    openSalaReleaseModal,
    requestReservationRoomSeat,
    handleReleaseReservationTables,
    handleFinalizeReservationService,
    setNoShowConfirmation,
    selectedDateKey,
    setWaitlistCount,
    openWaitlistSeatModal,
    waitlistRefreshKey,
  } = props;

  return (
<div className={`room-today-reservations room-today-reservations--${effectiveBoardFilter} ${reservationsFeatureEnabled && waitlistFeatureEnabled ? 'room-today-reservations--multi-feature' : 'room-today-reservations--single-feature'}`}>
  <div className="reservo-board-filter" role="group" aria-label="Escolher o que exibir">
    <span className="reservo-board-filter-label">Exibir</span>
    <div className="reservo-board-filter-options">
      {reservationsFeatureEnabled && waitlistFeatureEnabled && (
      <button
        type="button"
        aria-pressed={effectiveBoardFilter === 'both'}
        className={`reservo-board-filter-button ${effectiveBoardFilter === 'both' ? 'active' : ''}`}
        onClick={() => setBoardFilter('both')}
      >
        <LayoutGrid size={16} />
        <span>Ambos</span>
      </button>
      )}
      {reservationsFeatureEnabled && (
      <button
        type="button"
        aria-pressed={effectiveBoardFilter === 'reservations'}
        className={`reservo-board-filter-button ${effectiveBoardFilter === 'reservations' ? 'active' : ''}`}
        onClick={() => setBoardFilter('reservations')}
      >
        <CalendarClock size={16} />
        <span>Reservas</span>
        <span className="reservo-board-filter-count">{dashboardSummary.reservationsToday}</span>
      </button>
      )}
      {waitlistFeatureEnabled && (
      <button
        type="button"
        aria-pressed={effectiveBoardFilter === 'waitlist'}
        className={`reservo-board-filter-button ${effectiveBoardFilter === 'waitlist' ? 'active' : ''}`}
        onClick={() => setBoardFilter('waitlist')}
      >
        <Hourglass size={16} />
        <span>Fila de espera</span>
        <span className="reservo-board-filter-count">{waitlistCount}</span>
      </button>
      )}
    </div>
  </div>

  <div className={`reservo-board ${effectiveBoardFilter === 'both' ? 'reservo-board--both' : 'reservo-board--single'}`}>
  {reservationsFeatureEnabled && (effectiveBoardFilter === 'both' || effectiveBoardFilter === 'reservations') && (
  <section className="reservo-board-column reservo-column-reservations">
  <div className="room-today-header">
    <div className="room-today-title-group">
      <div className="room-today-title-row">
        <h3>Reservas do Dia</h3>
        <div className="room-today-toolbar">
          {requiresClientConfirmation && (
            <label htmlFor="today-confirmed-only" className="room-today-confirmation-toggle">
              <input
                id="today-confirmed-only"
                type="checkbox"
                checked={todayConfirmedOnly}
                onChange={(e) => setTodayConfirmedOnly(e.target.checked)}
              />
              <span>Apenas confirmadas</span>
            </label>
          )}
          <div className="room-today-filter">
            <label htmlFor="today-room-filter">Ambiente</label>
            <select
              id="today-room-filter"
              className="form-control"
              value={todayRoomFilter}
              onChange={(e) => setTodayRoomFilter(e.target.value)}
            >
              <option value="all">Todos os ambientes</option>
              {floors.map((floor) => (
                <option key={floor.id} value={String(floor.id)}>
                  {floor.name}
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            className="room-today-add-button"
            onClick={() => setShowReservationModal(true)}
          >
            Adicionar Reserva
          </button>
        </div>
      </div>
      <p className="room-today-title-description">
        Dados e ocupações exibidos para {selectedDateLabel}.
      </p>
      <div className="room-today-tabs" role="tablist" aria-label="Abas das reservas do dia selecionado">
        {todayReservationTabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            id={`today-reservations-tab-${tab.id}`}
            aria-controls="today-reservations-panel"
            aria-selected={todayReservationTab === tab.id}
            className={`room-today-tab ${todayReservationTab === tab.id ? 'active' : ''}`}
            onClick={() => setTodayReservationTab(tab.id)}
          >
            <span className="room-today-tab-label">
              {todayReservationTab === tab.id && <CheckCircle size={16} aria-hidden="true" />}
              {tab.label}
            </span>
            <span className="room-today-tab-count">{tab.count}</span>
          </button>
        ))}
      </div>
    </div>
  </div>
  <div
    id="today-reservations-panel"
    className="room-today-panel"
    role="tabpanel"
    aria-labelledby={`today-reservations-tab-${activeTodayTab?.id || 'upcoming'}`}
  >
      {activeTodayReservations.length === 0 && (
        <div className="room-empty">{todayReservationsEmptyMessage}</div>
      )}
      {activeTodayReservations.length > 0 && (
        <ul className="room-today-list">
          {activeTodayReservations.map(res => {
              const room = floors.find((floor) => String(floor.id) === String(res.roomId));
              const usarMapaMesas = usesTableMap(room, legacyMapDefault);
              const reservationId = String(res.id);
              const mesasSelecionadas = awaitingOcupacoesByReservationId.get(reservationId) || [];
              const mesasSentadas = seatedOcupacoesByReservationId.get(reservationId) || [];
              const selectedMesaIds = extractMesaIdsFromOcupacoes(mesasSelecionadas);
              const seatedMesaIds = extractMesaIdsFromOcupacoes(mesasSentadas);
              const activeMesaIds = [...new Set([...seatedMesaIds, ...selectedMesaIds])];
              const salaOcupacao = salaOcupacaoByReservationId.get(reservationId) || null;
              const isSeated = usarMapaMesas
                ? mesasSentadas.length > 0
                : Boolean(salaOcupacao);
              const selectedTableName = selectedMesaIds.length > 0
                ? resolveTableDisplayNames(selectedMesaIds)
                : null;
              const seatedTableName = seatedMesaIds.length > 0
                ? resolveTableDisplayNames(seatedMesaIds)
                : null;
              const shouldShowReservedTable = Boolean(
                usarMapaMesas
                &&
                res.tableName
                && (!selectedTableName || String(res.tableName) !== String(selectedTableName))
                && (!seatedTableName || String(res.tableName) !== String(seatedTableName))
              );
              const { displayStatus } = getReservationStatusMeta(
                res.status,
                requiresRestaurantConfirmation,
              );
              const customerDisplayStatus = isReservationCustomerConfirmed(res.statusCliente)
                ? 'confirmada'
                : 'pendente';
               const customerStatusLabel = STATUS_LABELS[customerDisplayStatus] || customerDisplayStatus;
               const isConcluded = displayStatus === 'concluida' || displayStatus === 'no_show';
               const finalizedStatusLabel = displayStatus === 'no_show'
                 ? 'Não compareceu'
                 : 'Reserva finalizada';
               const showCustomerConfirmationTag = requiresClientConfirmation;
              const isStatusUpdating = statusUpdatingId === reservationId;
              const canSelectTable = usarMapaMesas
                && !isUnavailableReservationStatus(res.status)
                && todayReservationTab !== 'seated';
              const canMarkNoShow = todayReservationTab === 'upcoming'
                && !isSeated
                && !isUnavailableReservationStatus(res.status);
              const canSeatPendingTables = usarMapaMesas
                && todayReservationTab === 'upcoming'
                && isSeated
                && selectedMesaIds.length > 0
                && !isUnavailableReservationStatus(res.status);
              const canFinalizeAllFromSeatedTab = usarMapaMesas
                && todayReservationTab === 'seated'
                && isSeated
                && seatedMesaIds.length > 1
                && !isUnavailableReservationStatus(res.status);
              const canFinalizeSeatedReservation = usarMapaMesas
                && todayReservationTab === 'upcoming'
                && isSeated
                && activeMesaIds.length > 0
                && !isUnavailableReservationStatus(res.status);
              const displayTime = res.time ? String(res.time).slice(0, 5) : '--:--';
              const whatsappLink = buildWhatsappLink(res.phone);
              return (
                 <li
                   key={res.id}
                   className={`room-today-item ${isConcluded ? 'is-concluded' : ''} ${displayStatus === 'no_show' ? 'is-no-show' : ''}`}
                   role="button"
                  tabIndex={0}
                  onClick={() => handleEditReservation(res.id)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      handleEditReservation(res.id);
                    }
                  }}
                >
                  <div className="room-today-main">
                    <div className="room-today-time-block">
                      <span className="room-today-time">{displayTime}</span>
                      <div className="room-today-guests">
                        <Users size={16} />
                        <span>{res.guests} pessoa{res.guests > 1 ? 's' : ''}</span>
                      </div>
                    </div>
                    <div className="room-today-details">
                      <div className="room-today-name">{res.name}</div>
                      <div className="room-today-meta">
                        {res.roomName && (
                          <span className="room-today-meta-pill is-room">{res.roomName}</span>
                        )}
                        {usarMapaMesas && seatedTableName && (
                          <span className="room-today-meta-pill is-seated-table">
                            {seatedTableName}
                          </span>
                        )}
                        {shouldShowReservedTable && (
                          <span className="room-today-meta-pill is-table">{res.tableName}</span>
                        )}
                        {usarMapaMesas && selectedTableName && (
                          <span className="room-today-meta-pill is-selected-table">
                            {selectedTableName}
                          </span>
                        )}
                        {whatsappLink && (
                          <a
                            className="room-today-meta-pill is-phone is-whatsapp-link"
                            href={whatsappLink}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(event) => event.stopPropagation()}
                            title={`Enviar WhatsApp para ${res.phone}`}
                          >
                            <img src={WHATSAPP_ICON_URL} alt="" aria-hidden="true" className="reservo-whatsapp-pill-icon" />
                            {res.phone}
                          </a>
                        )}
                      </div>
                      {res.notes && (
                        <div
                          className="room-today-observation"
                          title={res.notes}
                        >
                          <span className="room-today-observation-label">Obs:</span>
                          <span className="room-today-observation-text">{res.notes}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className={`room-today-actions ${showCustomerConfirmationTag ? '' : 'is-compact'} ${isConcluded ? 'is-finalized' : ''}`}>
                    {isConcluded && (
                      <div className={`room-today-finalized-state ${displayStatus === 'no_show' ? 'is-no-show' : ''}`}>
                        {displayStatus === 'no_show'
                          ? <UserX size={18} aria-hidden="true" />
                          : <CheckCircle size={18} aria-hidden="true" />}
                        <span>{finalizedStatusLabel}</span>
                      </div>
                    )}
                    {!isConcluded && showCustomerConfirmationTag && (
                      <div className="room-reservation-status">
                        <span className={`reservation-status-badge status-${customerDisplayStatus}`}>
                          {customerStatusLabel}
                        </span>
                      </div>
                    )}
                    {!isConcluded && (
                    <div className="room-today-action-stack">
                      <button
                        type="button"
                        className={`room-today-action ${isSeated ? 'is-danger' : ''}`}
                        onClick={async (event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          if (!usarMapaMesas) {
                            if (salaOcupacao) {
                              openSalaReleaseModal(salaOcupacao);
                            } else {
                              requestReservationRoomSeat(res);
                            }
                            return;
                          }

                          if (isSeated) {
                            if (seatedMesaIds.length > 1) {
                              openMesaSelectModal(res, 'leave');
                              return;
                            }

                            await handleReleaseReservationTables(res.id, seatedMesaIds);
                          } else {
                            openMesaSelectModal(res, 'seat');
                          }
                        }}
                        disabled={isStatusUpdating}
                      >
                        {isSeated ? <LogOut size={15} /> : <Armchair size={15} />}
                        {isSeated
                          ? (
                            usarMapaMesas && seatedMesaIds.length > 1
                              ? 'Levantar uma'
                              : 'Levantar'
                          )
                          : 'Sentar'}
                      </button>
                      {canSeatPendingTables && (
                        <button
                          type="button"
                          className="room-today-action is-secondary"
                          onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            openMesaSelectModal(res, 'seat');
                          }}
                          disabled={isStatusUpdating}
                        >
                          <Armchair size={15} />
                          Sentar
                        </button>
                      )}
                      {canFinalizeAllFromSeatedTab && (
                        <button
                          type="button"
                          className="room-today-action is-secondary"
                          onClick={async (event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            await handleFinalizeReservationService(res.id, activeMesaIds);
                          }}
                          disabled={isStatusUpdating}
                        >
                          <CheckCircle size={15} />
                          Levantar todas e finalizar
                        </button>
                      )}
                    {canSelectTable && (
                      <button
                        type="button"
                        className="room-today-action is-secondary"
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          openMesaSelectModal(res, 'select');
                        }}
                        disabled={isStatusUpdating}
                      >
                        <Table size={15} />
                        {selectedTableName ? 'Ajustar mesas' : 'Selecionar mesas'}
                      </button>
                    )}
                    {canMarkNoShow && (
                      <button
                        type="button"
                        className="room-today-action is-warning"
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          setNoShowConfirmation({
                            reservationId,
                            reservationName: res.name || `Reserva ${reservationId}`,
                            reservationTime: displayTime,
                          });
                        }}
                        disabled={isStatusUpdating}
                      >
                        <UserX size={15} />
                        Não compareceu
                      </button>
                    )}
                     {canFinalizeSeatedReservation && (
                      <button
                        type="button"
                        className="room-today-action is-warning"
                        onClick={async (event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          await handleFinalizeReservationService(res.id, activeMesaIds);
                        }}
                        disabled={isStatusUpdating}
                      >
                        <CheckCircle size={15} />
                        Levantar e finalizar
                      </button>
                     )}
                   </div>
                   )}
                  </div>
                </li>
              );
            })}
        </ul>
      )}
  </div>
  </section>
  )}

  {waitlistFeatureEnabled && (effectiveBoardFilter === 'both' || effectiveBoardFilter === 'waitlist') && (
  <section className="reservo-board-column reservo-column-waitlist">
    <WaitlistPanel
      key={selectedDateKey}
      dateKey={selectedDateKey}
      selectedDateLabel={selectedDateLabel}
      onCountChange={setWaitlistCount}
      onSeatEntry={openWaitlistSeatModal}
      refreshKey={waitlistRefreshKey}
    />
  </section>
  )}
  </div>
</div>

  );
};

export default DashboardTodayBoard;
